import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Flex,
  Box,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  VStack,
  Button,
  HStack,
  Tag,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { SearchIcon, TriangleDownIcon } from '@chakra-ui/icons'
import * as React from 'react'
import {
  useCreateDeviceGroupMutation,
  useGetLocationDevicesQuery,
  useUpdateDeviceGroupMutation,
} from '../../../api/api'
import { useEffect, useState, useMemo } from 'react'
import { DeviceCheckbox } from '../../utils'
import { toast } from 'react-toastify'

type Mode = 'create' | 'edit'

export function DeviceGroupModal({
  isOpen,
  onClose,
  mode = 'create',
  initialGroup,
}: {
  isOpen: boolean
  onClose: () => void
  mode?: Mode
  initialGroup?: { id: number; name: string; deviceIds: number[] }
  onConfirm?: (payload: {
    mode: Mode
    groupId?: number
    groupName: string
    deviceIds: number[]
  }) => void
}) {
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([])
  const [groupName, setGroupName] = useState('')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useGetLocationDevicesQuery()

  const [createDeviceGroup] = useCreateDeviceGroupMutation()
  const [updateDeviceGroup] = useUpdateDeviceGroupMutation()

  const locations = useMemo(() => {
    const payload = data ?? []

    return payload.map((location) => ({
      id: location.id,
      title: location.title,
      departmentName: location.department.name,
      devices: location.devices ?? [],
    }))
  }, [data])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setStep(1)

    if (mode === 'edit' && initialGroup) {
      setSelectedDeviceIds(initialGroup.deviceIds ?? [])
      setGroupName(initialGroup.name ?? '')
    } else {
      setSelectedDeviceIds([])
      setGroupName('')
    }

    setSearch('')
  }, [isOpen, mode, initialGroup])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) {
      return locations
    }

    return locations
      .map((x) => ({
        ...x,
        devices: x.devices.filter((d) => d.title.toLowerCase().includes(term)),
      }))
      .filter(
        (x) =>
          x.title.toLowerCase().includes(term) ||
          (x.departmentName ?? '').toLowerCase().includes(term) ||
          x.devices.length > 0,
      )
  }, [search, locations])

  const toggleDevice = (id: number) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const suggestions = ['My Shelly', 'Shelly Office', 'Sofia Office - Shelly']

  const handleDiscard = () => {
    onClose()
  }

  const handleNext = () => {
    if (selectedDeviceIds.length > 0) setStep(2)
  }

  const handleBack = () => setStep(1)

  const handleConfirm = async () => {
    if (!groupName.trim()) {
      return
    }

    const body = { name: groupName.trim(), device_ids: selectedDeviceIds }

    if (mode === 'create') {
      try {
        await createDeviceGroup(body).unwrap()
        toast.success(`Group created successfully`, {
          style: { padding: '10px 20px' },
          isLoading: false,
          autoClose: 3000,
        })
        onClose()
      } catch (error: any) {
        toast.error(
          `${error?.data?.message ? error.data.message : 'Something went wrong!'}`,
          {
            style: { padding: '10px 20px' },
            isLoading: false,
            autoClose: 3000,
          },
        )
      }
    } else {
      const toastId = toast.info('Updating group...', {
        style: { padding: '10px 20px' },
        autoClose: false,
        isLoading: true,
        progress: undefined,
      })
      try {
        await updateDeviceGroup({ ...body, id: initialGroup!.id }).unwrap()
        toast.update(toastId, {
          render: `Group updated successfully`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
        onClose()
      } catch (error: any) {
        toast.update(toastId, {
          render: `${
            error?.data?.message ? error.data.message : 'Something went wrong!'
          }`,
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        })
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader pb={2}>
          <Text fontWeight="bold">
            {mode === 'edit' ? 'Edit device group' : 'Create device group'}
          </Text>

          <HStack mt={1} spacing={2}>
            {[1, 2].map((i) => (
              <Box
                key={i}
                w={step === i ? '18px' : '6px'}
                h="6px"
                borderRadius="full"
                bg={step === i ? 'blue.500' : '#CECFD0'}
                transition="all 0.15s ease"
                p="1px"
              />
            ))}
            <Text fontFamily="Inter" fontWeight={400} fontSize="16px" color="#707070">
              {step === 1 ? 'Select device/s' : 'Name your device group'}
            </Text>
          </HStack>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody pt={2}>
          {step === 1 && (
            <>
              <InputGroup mb={4}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon />
                </InputLeftElement>
                <Input
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  borderRadius="full"
                />
              </InputGroup>

              {isLoading ? (
                <Flex align="center" justify="center" py={8}>
                  <Spinner />
                </Flex>
              ) : (
                <Accordion allowMultiple w="full">
                  {filtered.map((location, idx) => (
                    <AccordionItem key={location.id} border="none">
                      {({ isExpanded }) => (
                        <>
                          <h2>
                            <AccordionButton
                              px={0}
                              _hover={{ bg: 'transparent' }}
                              _focus={{ boxShadow: 'none' }}
                              _focusVisible={{ boxShadow: 'none' }}
                              alignItems="flex-start"
                            >
                              <HStack spacing={2} flex="1" align="flex-start">
                                <TriangleDownIcon
                                  boxSize="10px"
                                  color="gray.700"
                                  transform={
                                    isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                                  }
                                  transition="transform 0.15s ease"
                                  mt="6px"
                                />

                                <Box flex="1" textAlign="left">
                                  <Text
                                    fontFamily="Inter"
                                    fontWeight={700}
                                    fontSize="14px"
                                    color="#484848"
                                  >
                                    {location.title}
                                  </Text>

                                  {location.departmentName && (
                                    <Text
                                      fontFamily="Inter"
                                      fontSize="12px"
                                      color="#9B9B9B"
                                    >
                                      {location.departmentName}
                                    </Text>
                                  )}
                                </Box>
                              </HStack>
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={4} pl={1}>
                            <VStack align="start" spacing={3}>
                              {location.devices.map((device) => (
                                <DeviceCheckbox
                                  key={device.id}
                                  isChecked={selectedDeviceIds.includes(device.id)}
                                  onChange={() => toggleDevice(device.id)}
                                  colorScheme="blue"
                                >
                                  {device.title}
                                </DeviceCheckbox>
                              ))}
                            </VStack>
                          </AccordionPanel>

                          {idx < filtered.length - 1 && (
                            <Box h="1px" bg="gray.200" ml={0} mr={0} />
                          )}
                        </>
                      )}
                    </AccordionItem>
                  ))}
                  {filtered.length === 0 && (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      No locations or devices match your search.
                    </Text>
                  )}
                </Accordion>
              )}
            </>
          )}

          {step === 2 && (
            <VStack align="start" spacing={4}>
              <Input
                placeholder="Enter a group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                borderRadius="md"
              />

              <HStack spacing={2} wrap="wrap">
                <Box>
                  <Text mb={2} fontSize="sm" color="gray.500">
                    Suggestions
                  </Text>

                  <Wrap spacing="10px">
                    {suggestions.map((s) => (
                      <WrapItem key={s}>
                        <Tag
                          borderRadius="full"
                          bg="#F2F4F7"
                          color="#475467"
                          px="12px"
                          py="6px"
                          fontSize="14px"
                          fontWeight={500}
                          cursor="pointer"
                          _hover={{ bg: '#EAECF0' }}
                          _active={{ bg: '#D0D5DD' }}
                          _focusVisible={{ boxShadow: 'none' }}
                          onClick={() => setGroupName(s)}
                        >
                          {s}
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              </HStack>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          {step === 1 && (
            <>
              <Button variant="ghost" onClick={handleDiscard}>
                Discard
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleNext}
                isDisabled={selectedDeviceIds.length === 0}
              >
                Continue
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <Button variant="ghost" onClick={handleBack}>
                Back
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirm}
                isDisabled={!groupName.trim()}
              >
                {mode === 'edit' ? 'Save' : 'Create'}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
